import { isDefiniteShopRejection } from "./shopMutationOutcome";
import { useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import { ShopApiError, shopAdminApi, type ProcessedProductDraft, type ProcessedProductSnapshot } from "./shopAdminApi";
import { validProcessedProductDraft } from "./processedProductModel";

export function useProcessedProductWriter(user: User | null | undefined) {
  const [loading, setLoading] = useState(false), [saving, setSaving] = useState(false), [pending, setPending] = useState(false);
  const [conflict, setConflict] = useState(false), [version, setVersion] = useState(""), [reason, setReason] = useState(""), [error, setError] = useState("");
  const epoch = useRef(0), inFlight = useRef(false);
  const attempted = useRef<{ documentId?: string; actorUid: string; uncertain?: boolean; body: { draft: ProcessedProductDraft; reason: string; expectedDocumentVersion: string }; key: string } | null>(null);
  useEffect(() => () => { epoch.current++; }, []);
  const reset = () => {
    if (inFlight.current || attempted.current) return;
    epoch.current++; setLoading(false); setSaving(false); setPending(false); setConflict(false); setVersion(""); setReason(""); setError("");
  };
  const load = async (documentId: string, hydrate: (snapshot: ProcessedProductSnapshot) => void) => {
    if (inFlight.current || attempted.current) return;
    reset(); setLoading(true); const current = epoch.current;
    try {
      if (!user) throw new ShopApiError(401, "unauthenticated");
      const snapshot = await shopAdminApi.processedProduct(user, documentId);
      if (current !== epoch.current) return;
      hydrate(snapshot); setVersion(snapshot.expectedDocumentVersion);
    } catch { if (current === epoch.current) setError("Aktuella produktuppgifter kunde inte hämtas. Kontrollera API-anslutning och behörighet."); }
    finally { if (current === epoch.current) setLoading(false); }
  };
  const save = async (documentId: string | undefined, prepare: () => Promise<ProcessedProductDraft>): Promise<boolean> => {
    if (!user || inFlight.current || loading || conflict || (!attempted.current && (!reason.trim() || reason.trim().length > 500 || (documentId && !version)))) return false;
    inFlight.current = true; setSaving(true); setPending(true); setError(""); const current = epoch.current;
    try {
      if (!attempted.current) {
        // Preparation uploads once. No API request exists if preparation fails.
        try {
          const draft = await prepare(); if (!validProcessedProductDraft(draft)) throw new Error("invalid_product_draft");
          if (current !== epoch.current) return false;
          attempted.current = { documentId, actorUid: user.uid, body: { draft: structuredClone(draft), reason: reason.trim(), expectedDocumentVersion: version }, key: crypto.randomUUID() };
        }
        catch { if (current === epoch.current) { setPending(false); setError("Produktuppgifter eller bilder kunde inte förberedas. Kontrollera fälten och försök igen."); } return false; }
      }
      const frozen = attempted.current;
      if (frozen.actorUid !== user.uid) { setError("Logga in som användaren som påbörjade sparandet för att bekräfta samma åtgärd. Granska annars serverns historik innan en ny produkt skapas."); return false; }
      if (frozen.documentId) await shopAdminApi.updateProcessedProduct(user, frozen.documentId, frozen.body, frozen.key);
      else await shopAdminApi.createProcessedProduct(user, { draft: frozen.body.draft, reason: frozen.body.reason }, frozen.key);
      if (current !== epoch.current) return false;
      attempted.current = null; setPending(false); return true;
    } catch (caught) {
      if (current !== epoch.current) return false;
      if (isDefiniteShopRejection(caught, attempted.current?.uncertain)) {
        attempted.current = null; setPending(false); setConflict(true);
        setError(caught instanceof ShopApiError && (caught.status === 401 || caught.status === 403) ? "Du saknar aktuell behörighet att spara produkten." :
          "Uppgifterna har ändrats eller ändringen är spärrad. En använd produkts process och råvarukopplingar måste behållas. Hämta aktuella uppgifter och granska igen.");
      } else { if (attempted.current) attempted.current.uncertain = true; setError("Sparandet kunde inte bekräftas. Försök igen med samma uppgifter och uppladdade bilder innan du gör en ny ändring."); }
      return false;
    } finally { inFlight.current = false; if (current === epoch.current) setSaving(false); }
  };
  const retry = () => {
    const frozen = attempted.current;
    return frozen ? save(frozen.documentId, async () => frozen.body.draft) : Promise.resolve(false);
  };
  return { loading, saving, pending, conflict, version, reason, setReason, error, reset, load, save, retry };
}
